import { Stack, Typography } from '@mui/material';
import React from 'react';
import {
    DashboardActivityChart,
    DashboardKpiCard,
    DashboardKpiRow,
    useAllowedPath
} from '../../sections/@dashboard/app';
import { fNumber, formatCurrency } from '../../utils/formatNumber';
import i18n from '../../i18n';

// "Received 1 200,00 € · Paid out 300,00 €": the figures that qualify a card's
// main one, in one line.
const line = (parts) =>
    parts
        .filter(Boolean)
        .map(([label, value]) => `${label} ${value}`)
        .join(' · ');

/**
 * A gateway user's dashboard: their own gateway's totals (`data.totals`, from
 * public.gateway_totals) and its traffic chart. The takings are the gateway's
 * wallet takings summed, in its currency, so they match the Wallets grid (transfers
 * between its wallets cancel out in the sum). The balance is the gateway's — only
 * a gateway has one: what the owner owes, in the balance currency, negative when
 * prepaid (an advance), and, when advance payments are on, what payins are limited
 * by. Counts are payins and payouts by success / pending / failed.
 *
 * Each card leads with the figure the gateway acts on; the rest of its group is
 * the line under it.
 */
export default function GatewayDashboardContent({ data, isLoading }) {
    const allowed = useAllowedPath();
    const totals = data?.totals || {};
    const currency = totals.currencyCode || undefined;
    const balanceCurrency = totals.balanceCurrencyCode || undefined;
    const transactionsByDate = data?.transactionsByDate || {};
    const labels = data?.datesLabelsPayments || [];

    const t = (key) => i18n.t(`dashboard.gateway.${key}`);
    const money = (key) => formatCurrency(totals[key] || 0, currency);
    const flow = (prefix) =>
        line([
            [t('pending'), `${fNumber(totals[`${prefix}PendingCount`] || 0)} (${money(`${prefix}PendingAmount`)})`],
            [t('failed'), fNumber(totals[`${prefix}FailedCount`] || 0)]
        ]);

    const cards = [
        {
            key: 'balance',
            title: t('balance.available'),
            tooltip: t('balance.availableTooltip'),
            value: money('available'),
            secondary: line([
                [t('balance.received'), money('received')],
                [t('balance.paidOut'), money('paidOut')],
                [t('balance.held'), money('held')]
            ]),
            icon: 'eva:credit-card-outline',
            color: 'primary'
        },
        {
            key: 'gatewayBalance',
            title: totals.advanceEnabled ? t('gatewayBalance.titleLimiting') : t('gatewayBalance.title'),
            tooltip: t('gatewayBalance.tooltip'),
            value: formatCurrency(totals.balance || 0, balanceCurrency),
            icon: 'eva:trending-up-outline',
            color: totals.advanceEnabled ? 'warning' : 'secondary'
        },
        {
            key: 'payins',
            title: `${t('payins.title')} — ${t('success')}`,
            value: fNumber(totals.payinSuccessCount || 0),
            secondary: flow('payin'),
            icon: 'eva:arrow-downward-outline',
            color: 'info',
            to: allowed('/app/payin-instances')
        },
        {
            key: 'payouts',
            title: `${t('payouts.title')} — ${t('success')}`,
            value: fNumber(totals.payoutSuccessCount || 0),
            secondary: flow('payout'),
            icon: 'eva:arrow-upward-outline',
            color: 'warning',
            to: allowed('/app/payout-instances')
        },
        {
            key: 'wallets',
            title: t('wallets.active'),
            value: fNumber(totals.walletsActive || 0),
            secondary: line([[t('wallets.total'), fNumber(totals.walletsTotal || 0)]]),
            icon: 'eva:briefcase-outline',
            color: 'success',
            to: allowed('/app/wallets')
        }
    ];

    return (
        <Stack spacing={3}>
            <Stack spacing={1.5}>
                {/* The gateway's name heads the row rather than one card,
                    where it cut the title short. */}
                {totals.gatewayName && (
                    <Typography variant="body2" color="text.secondary">
                        {`${t('balance.title')} — ${totals.gatewayName}`}
                    </Typography>
                )}
                <DashboardKpiRow>
                    {cards.map(({ key, ...card }) => (
                        <DashboardKpiCard key={key} loading={isLoading} {...card} />
                    ))}
                </DashboardKpiRow>
            </Stack>
            <DashboardActivityChart
                loading={isLoading}
                title={t('byDate')}
                labels={labels}
                series={Object.entries(transactionsByDate).map(([name, series]) => ({
                    name: i18n.t(`dashboard.${name}`),
                    data: series
                }))}
            />
        </Stack>
    );
}
